import {
  Icon,
  HStack,
  Text,
  Stack,
  Box,
  Collapse,
  Button,
} from '@chakra-ui/react';
import { Link } from '@chakra-ui/next-js';
import React, { useMemo, useState, useCallback } from 'react';
import { NEARBY } from '@habity/constants';
import { NestedHome, NearbyType, NearbyResult } from '@habity/types';
import { getNearbyButtonIcon } from '~/constants';
import { ShowHideButton } from '~/components/ui/Buttons';
import { useAppStore } from '~/store/useAppStore';
import { NEARBY_DISTANCE_THRESHOLD } from '~/lib/homes/profile/nearbyConstants';
import {
  deduplicateNearbyItems,
  groupNearbyByType,
  createGoogleMapsLink,
} from '~/lib/homes/profile/nearbyHelpers';
import { useUser } from '~/hooks/useUser';
import { isBrokerOrAdmin } from '~/lib/roles';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { patchAPI } from '~/lib/api';
import { revalidateHome } from '~/app/actions/revalidate';

type NearbyItemWithMeta = NearbyResult & { isHidden?: boolean };

type NearbyGroupData = {
  type: NearbyType;
  title: string;
  items: NearbyItemWithMeta[];
};

const NearbyItem = ({
  item,
  city,
  zip,
  canEdit,
  onToggleVisibility,
  isToggling,
}: {
  item: NearbyItemWithMeta;
  city?: string;
  zip?: string;
  canEdit: boolean;
  onToggleVisibility: (placeId: string) => void;
  isToggling: boolean;
}) => {
  const roundedDistance = Math.round(item.distance / 5) * 5;
  const isHidden = item.isHidden;

  return (
    <HStack
      justify="space-between"
      opacity={isHidden ? 0.4 : 1}
      transition="opacity 0.2s"
    >
      <Link
        href={createGoogleMapsLink(item.name, item.address?.name, city, zip)}
        target="_blank"
      >
        <Text
          fontSize="sm"
          fontWeight="light"
          color="blackAlpha.600"
          textDecoration={isHidden ? 'line-through' : undefined}
        >
          {item.name}
        </Text>
      </Link>
      <HStack spacing="2">
        <Text fontSize="sm" color="blackAlpha.700">
          {roundedDistance} m
        </Text>
        {canEdit && (
          <Button
            size="xs"
            variant="secondary"
            leftIcon={<Icon as={isHidden ? MdVisibilityOff : MdVisibility} />}
            aria-label={isHidden ? 'Visa plats' : 'Dölj plats'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleVisibility(item.place_id);
            }}
            isLoading={isToggling}
            color={isHidden ? 'blackAlpha.400' : 'black'}
          >
            {isHidden ? 'Visa' : 'Dölj'}
          </Button>
        )}
      </HStack>
    </HStack>
  );
};

const NearbyGroup = ({
  group,
  city,
  zip,
  canEdit,
  onToggleVisibility,
  togglingPlaceId,
}: {
  group: NearbyGroupData;
  city?: string;
  zip?: string;
  canEdit: boolean;
  onToggleVisibility: (placeId: string) => void;
  togglingPlaceId: string | null;
}) => {
  const [expanded, setExpanded] = useState(false);
  const closest = group.items[0];
  const rest = group.items.slice(1);
  const hasMore = rest.length > 0;
  const roundedDistance = Math.round(closest.distance / 5) * 5;
  const isClosestHidden = closest.isHidden;

  return (
    <Stack gap="0.5">
      {/* Header row: icon + title + count */}
      <HStack
        spacing="2"
        cursor={hasMore ? 'pointer' : undefined}
        onClick={hasMore ? () => setExpanded(!expanded) : undefined}
      >
        <Icon as={getNearbyButtonIcon(group.type)} boxSize="5" />
        <Text fontSize="sm" fontWeight="medium">
          {group.title}
          {hasMore && (
            <Text as="span" color="blackAlpha.500" fontWeight="normal">
              {' '}
              ({group.items.length})
            </Text>
          )}
        </Text>
      </HStack>

      {/* Closest item: name + distance + toggle on same row */}
      <HStack
        justify="space-between"
        ml="7"
        opacity={isClosestHidden ? 0.4 : 1}
        transition="opacity 0.2s"
      >
        <Link
          href={createGoogleMapsLink(
            closest.name,
            closest.address?.name,
            city,
            zip
          )}
          target="_blank"
        >
          <Text
            fontSize="sm"
            fontWeight="light"
            color="blackAlpha.600"
            textDecoration={isClosestHidden ? 'line-through' : undefined}
          >
            {closest.name}
          </Text>
        </Link>
        <HStack spacing="2">
          <Text fontSize="sm" color="blackAlpha.700">
            {roundedDistance} m
          </Text>
          {canEdit && (
            <Button
              size="xs"
              variant="secondary"
              leftIcon={
                <Icon as={isClosestHidden ? MdVisibilityOff : MdVisibility} />
              }
              aria-label={isClosestHidden ? 'Visa plats' : 'Dölj plats'}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleVisibility(closest.place_id);
              }}
              isLoading={togglingPlaceId === closest.place_id}
              color={isClosestHidden ? 'blackAlpha.400' : 'black'}
            >
              {isClosestHidden ? 'Visa' : 'Dölj'}
            </Button>
          )}
        </HStack>
      </HStack>

      {/* Expanded sub-items with Collapse animation */}
      {hasMore && (
        <Collapse in={expanded} animateOpacity>
          <Stack gap="0.5" ml="7">
            {rest.map((item, i) => (
              <NearbyItem
                key={item.place_id || i}
                item={item}
                city={city}
                zip={zip}
                canEdit={canEdit}
                onToggleVisibility={onToggleVisibility}
                isToggling={togglingPlaceId === item.place_id}
              />
            ))}
          </Stack>
        </Collapse>
      )}
    </Stack>
  );
};

export const NearbyGoogle = ({ home }: { home: NestedHome }) => {
  const [showAll, setShowAll] = useState(false);
  const [togglingPlaceId, setTogglingPlaceId] = useState<string | null>(null);
  const [hiddenPlaceIds, setHiddenPlaceIds] = useState<string[]>(
    () => (home as any).hidden_nearby_place_ids || []
  );
  const isMobile = useAppStore((state) => state.isMobile);
  const { user } = useUser();
  const { nearby_json, address } = home || {};
  const { city, zip } = address || {};

  const canEdit = useMemo(() => {
    return !!user && isBrokerOrAdmin(user);
  }, [user]);

  const handleToggleVisibility = useCallback(
    async (placeId: string) => {
      if (!home.id) return;

      // Optimistic update
      const prevHidden = hiddenPlaceIds;
      const isCurrentlyHidden = prevHidden.includes(placeId);
      const newHidden = isCurrentlyHidden
        ? prevHidden.filter((id) => id !== placeId)
        : [...prevHidden, placeId];
      setHiddenPlaceIds(newHidden);

      try {
        const response = await patchAPI(
          `/providers/supabase/homes/${home.id}/toggle-nearby-visibility`,
          JSON.stringify({ placeId })
        );

        if (response?.data?.success) {
          setHiddenPlaceIds(response.data.hidden_nearby_place_ids || []);
          revalidateHome(home);
        } else {
          setHiddenPlaceIds(prevHidden);
        }
      } catch (error) {
        console.error('Failed to toggle nearby visibility:', error);
        setHiddenPlaceIds(prevHidden);
      }
    },
    [home, hiddenPlaceIds]
  );

  const groups = useMemo(() => {
    if (!nearby_json || nearby_json.length === 0) return [];

    const validTypes = new Set(NEARBY.map((n) => n.key));
    const validItems = nearby_json.filter(
      (item) => item.type && validTypes.has(item.type) && item.name
    );
    if (validItems.length === 0) return [];

    const deduped = deduplicateNearbyItems(validItems);

    // Add isHidden flag to each item
    const itemsWithHidden: NearbyItemWithMeta[] = deduped.map((item) => ({
      ...item,
      isHidden: hiddenPlaceIds.includes(item.place_id),
    }));

    // For non-editors, filter out hidden items
    const visibleItems = canEdit
      ? itemsWithHidden
      : itemsWithHidden.filter((item) => !item.isHidden);

    if (visibleItems.length === 0) return [];

    // Distance filtering
    const minDistance = Math.min(...visibleItems.map((item) => item.distance));
    const filtered =
      minDistance < NEARBY_DISTANCE_THRESHOLD
        ? visibleItems.filter(
            (item) => item.distance < NEARBY_DISTANCE_THRESHOLD
          )
        : visibleItems;

    // Group by type
    const grouped: Record<NearbyType, NearbyItemWithMeta[]> = {} as any;
    filtered.forEach((item) => {
      if (!grouped[item.type]) {
        grouped[item.type] = [];
      }
      grouped[item.type].push(item);
    });

    const nearbyTypeMap = new Map(NEARBY.map((n) => [n.key, n]));

    // Build sorted group list
    return Object.entries(grouped)
      .map(([type, items]) => ({
        type: type as NearbyType,
        title: nearbyTypeMap.get(type as NearbyType)?.title || type,
        items: [...items].sort((a, b) => a.distance - b.distance),
      }))
      .sort((a, b) => a.items[0].distance - b.items[0].distance);
  }, [nearby_json, hiddenPlaceIds, canEdit]);

  if (groups.length === 0) return null;

  const hasMany = groups.length > (isMobile ? 3 : 5);

  return (
    <Stack py="4" maxW="420px">
      <Collapse
        startingHeight={isMobile ? 180 : 300}
        in={hasMany ? showAll : true}
      >
        <Stack gap="8">
          {groups.map((group) => (
            <NearbyGroup
              key={group.type}
              group={group}
              city={city ?? undefined}
              zip={zip ?? undefined}
              canEdit={canEdit}
              onToggleVisibility={handleToggleVisibility}
              togglingPlaceId={togglingPlaceId}
            />
          ))}
        </Stack>
      </Collapse>

      {hasMany && (
        <Box ml="-12px" mt="2">
          <ShowHideButton
            variant="plain"
            show={showAll}
            onClick={() => setShowAll(!showAll)}
            count={groups.length}
          />
        </Box>
      )}
    </Stack>
  );
};
